/**
 * Optional fields your worker may persist on `serviceRequest.aiAnalysis` alongside `summary`.
 * Use JSON mode from Gemini, then map into these keys when saving `aiAnalysis`.
 *
 * Supported shapes (camelCase or snake_case from API):
 * - missingInfo / missing_information / gaps — string[] or newline-separated string
 * - clarifyingQuestions / clarifying_questions / questions
 * - risks / risk_factors
 * - nextSteps / next_steps / recommended_next_steps
 * - effortBand / effort_band / effort (when not used as level) — timeline note, e.g. "4–8 weeks (indicative)"
 * - technicalEffort / technical_effort / complexity / difficulty — easy | medium | hard (technical complexity)
 * - keywords / keyword_tags / tags / key_phrases — string[] or comma / semicolon / pipe–separated string
 * - priceSuggestion / suggested_price / estimated_price — string or object { min, max, currency, label?, note? }
 */

/** @returns {"" | "easy" | "medium" | "hard"} */
export function normalizeTechnicalEffortLevel(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim().toLowerCase();
  if (!s) return "";
  if (/^(easy|low|simple|s|1)$/.test(s) || s.includes("easy") || s.includes("low complexity")) return "easy";
  if (/^(medium|moderate|med|m|mid|2)$/.test(s) || s.includes("medium")) return "medium";
  if (/^(hard|high|complex|difficult|heavy|l|3)$/.test(s) || s.includes("hard") || s.includes("high complexity"))
    return "hard";
  return "";
}

function asStringArray(v) {
  if (Array.isArray(v)) {
    return v.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof v === "string" && v.trim()) {
    return v
      .split(/\n/)
      .map((line) => line.replace(/^\s*[-*•]\s*/, "").trim())
      .filter(Boolean);
  }
  return [];
}

function firstDefined(obj, keys) {
  for (const k of keys) {
    if (obj[k] != null && obj[k] !== "") return obj[k];
  }
  return undefined;
}

/** Dedupe case-insensitively; preserve first-seen casing. */
function normalizeKeywordListFromObject(o) {
  const raw = firstDefined(o, [
    "keywords",
    "keywordTags",
    "keyword_tags",
    "tags",
    "keyPhrases",
    "key_phrases",
  ]);
  if (Array.isArray(raw)) {
    return dedupeKeywords(raw.map((x) => String(x)));
  }
  if (typeof raw === "string" && raw.trim()) {
    return dedupeKeywords(raw.split(/[,;|]/).map((s) => s.trim()));
  }
  return [];
}

function dedupeKeywords(arr) {
  const seen = new Set();
  const out = [];
  for (const s of arr) {
    const t = String(s).trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

function safeFormatCurrency(amount, currency) {
  const c = String(currency || "EUR").trim() || "EUR";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: c,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${c}`.trim();
  }
}

/**
 * @returns {{ hasPrice: boolean, displayLine: string, detail: string, currency: string, min: number | null, max: number | null }}
 */
export function normalizePriceSuggestionFromObject(o) {
  const empty = {
    hasPrice: false,
    displayLine: "",
    detail: "",
    currency: "",
    min: null,
    max: null,
  };
  const raw = firstDefined(o, [
    "priceSuggestion",
    "price_suggestion",
    "suggestedPrice",
    "suggested_price",
    "estimatedPrice",
    "estimated_price",
    "pricingEstimate",
    "pricing_estimate",
  ]);
  if (raw == null || raw === "") return empty;
  if (typeof raw === "string" && raw.trim()) {
    return {
      hasPrice: true,
      displayLine: raw.trim(),
      detail: "",
      currency: "",
      min: null,
      max: null,
    };
  }
  if (typeof raw === "object" && raw !== null) {
    const r = /** @type {Record<string, unknown>} */ (raw);
    const currency = String(firstDefined(r, ["currency", "curr"]) || "EUR").trim() || "EUR";
    const minRaw = firstDefined(r, ["min", "amountMin", "amount_min", "low"]);
    const maxRaw = firstDefined(r, ["max", "amountMax", "amount_max", "high"]);
    const min = typeof minRaw === "number" && !Number.isNaN(minRaw) ? minRaw : null;
    const max = typeof maxRaw === "number" && !Number.isNaN(maxRaw) ? maxRaw : null;
    const label = typeof r.label === "string" ? r.label.trim() : "";
    const detailRaw = firstDefined(r, ["note", "rationale", "details", "explanation"]);
    const detail = typeof detailRaw === "string" ? detailRaw.trim() : "";
    const rangeStr = typeof r.range === "string" ? r.range.trim() : "";

    let displayLine = label;
    if (!displayLine && (min != null || max != null)) {
      if (min != null && max != null && min !== max) {
        displayLine = `${safeFormatCurrency(min, currency)} – ${safeFormatCurrency(max, currency)}`;
      } else if (min != null) {
        displayLine = safeFormatCurrency(min, currency);
      } else if (max != null) {
        displayLine = safeFormatCurrency(max, currency);
      }
    }
    if (!displayLine && rangeStr) displayLine = rangeStr;
    if (!displayLine) {
      if (detail) return { hasPrice: false, displayLine: "", detail, currency, min, max };
      return empty;
    }
    return { hasPrice: true, displayLine, detail, currency, min, max };
  }
  return empty;
}

/**
 * @param {Record<string, unknown> | null | undefined} ai
 */
export function normalizeAiInsights(ai) {
  if (!ai || typeof ai !== "object") {
    return {
      missingInfo: [],
      clarifyingQuestions: [],
      risks: [],
      nextSteps: [],
      effortBand: "",
      technicalEffort: /** @type {"" | "easy" | "medium" | "hard"} */ (""),
      keywords: [],
      price: normalizePriceSuggestionFromObject({}),
    };
  }
  const o = /** @type {Record<string, unknown>} */ (ai);
  const effortRaw = firstDefined(o, [
    "effortBand",
    "effort_band",
    "effort_timeline",
    "timelineNote",
    "timeline_note",
    "effort_indication",
  ]);
  let effortBand =
    typeof effortRaw === "string"
      ? effortRaw.trim()
      : effortRaw != null
        ? String(effortRaw).trim()
        : "";

  const levelRaw = firstDefined(o, [
    "technicalEffort",
    "technical_effort",
    "complexity",
    "difficulty",
    "effortLevel",
    "effort_level",
  ]);
  let technicalEffort = normalizeTechnicalEffortLevel(levelRaw);

  if (typeof o.effort === "string" && o.effort.trim()) {
    const lev = normalizeTechnicalEffortLevel(o.effort);
    if (lev) {
      if (!technicalEffort) technicalEffort = lev;
    } else if (!effortBand) {
      effortBand = o.effort.trim();
    }
  }

  return {
    missingInfo: asStringArray(firstDefined(o, ["missingInfo", "missing_information", "gaps"])),
    clarifyingQuestions: asStringArray(
      firstDefined(o, ["clarifyingQuestions", "clarifying_questions", "questions"])
    ),
    risks: asStringArray(firstDefined(o, ["risks", "risk_factors"])),
    nextSteps: asStringArray(
      firstDefined(o, ["nextSteps", "next_steps", "recommended_next_steps"])
    ),
    effortBand,
    technicalEffort,
    keywords: normalizeKeywordListFromObject(o),
    price: normalizePriceSuggestionFromObject(o),
  };
}

/**
 * If there is no `summary` string, build readable prose from structured lists so the main card still shows value.
 * @param {ReturnType<typeof normalizeAiInsights>} insights
 */
export function formatInsightsAsPlainText(insights) {
  const parts = [];
  if (insights.keywords?.length) {
    parts.push("Keywords\n\n" + insights.keywords.join(" · "));
  }
  if (insights.missingInfo.length) {
    parts.push(
      "Missing or unclear information\n\n" +
        insights.missingInfo.map((line) => `• ${line}`).join("\n")
    );
  }
  if (insights.clarifyingQuestions.length) {
    parts.push(
      "Suggested clarifying questions\n\n" +
        insights.clarifyingQuestions.map((line, i) => `${i + 1}. ${line}`).join("\n")
    );
  }
  if (insights.risks.length) {
    parts.push(
      "Risks and constraints\n\n" + insights.risks.map((line) => `• ${line}`).join("\n")
    );
  }
  if (insights.nextSteps.length) {
    parts.push(
      "Suggested next steps\n\n" + insights.nextSteps.map((line) => `• ${line}`).join("\n")
    );
  }
  if (insights.technicalEffort) {
    parts.push(
      `Technical complexity (indicative)\n\n${insights.technicalEffort.charAt(0).toUpperCase()}${insights.technicalEffort.slice(1)}`
    );
  }
  if (insights.effortBand) {
    parts.push(`Indicative effort / timeline\n\n${insights.effortBand}`);
  }
  if (insights.price?.hasPrice) {
    parts.push(`Indicative price range (not a quote)\n\n${insights.price.displayLine}`);
    if (insights.price.detail) {
      parts[parts.length - 1] += `\n\n${insights.price.detail}`;
    }
  }
  return parts.join("\n\n");
}

export function hasAnyInsights(insights) {
  return (
    (insights.keywords && insights.keywords.length > 0) ||
    insights.missingInfo.length > 0 ||
    insights.clarifyingQuestions.length > 0 ||
    insights.risks.length > 0 ||
    insights.nextSteps.length > 0 ||
    Boolean(insights.effortBand) ||
    Boolean(insights.technicalEffort) ||
    Boolean(insights.price?.hasPrice)
  );
}

function stripSimpleMarkdownForEmail(text) {
  return String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .trim();
}

/**
 * Plain-text first email draft when the client has not replied yet, using service-request `aiAnalysis`.
 * @param {Record<string, unknown> | null | undefined} ai
 * @returns {string | null}
 */
export function buildClientEmailDraftFromAiAnalysis(ai) {
  if (!ai || typeof ai !== "object") return null;
  const st = String(ai.status || "").trim().toLowerCase();
  if (st !== "ok" && st !== "completed" && st !== "success") return null;

  const summaryRaw = String(ai.summary || "").trim();
  const summary = stripSimpleMarkdownForEmail(summaryRaw);
  const insights = normalizeAiInsights(ai);
  const insightsBlock = formatInsightsAsPlainText(insights);
  const hasInsights = hasAnyInsights(insights);

  if (!summary && !hasInsights) return null;

  const lines = [
    "Hi,",
    "",
    "Thank you for reaching out. We've reviewed your request and wanted to follow up.",
  ];

  if (summary) {
    lines.push("", summary);
  }

  if (hasInsights) {
    lines.push("", "A few points we'd like to align on:", "", insightsBlock);
  }

  lines.push(
    "",
    "Happy to discuss scope, timeline, and next steps on a short call or by email — let us know what works best.",
    "",
    "Best regards"
  );

  return lines.join("\n").trim();
}

/** Labels for the 3-step technical scale (UX copy). */
export const TECHNICAL_EFFORT_STEPS = [
  { id: "easy", label: "Easy", hint: "Familiar stack, limited unknowns" },
  { id: "medium", label: "Medium", hint: "Some integration or scope risk" },
  { id: "hard", label: "Hard", hint: "Heavy unknowns or deep legacy" },
];
